/*
 * Purpose: Mission risk tiers — AI may devise freely; humans approve risky items before use
 * Last modified: 2026-06-28 | Agent
 * Completeness: 100
 */
import type { OutcomePlan, PlannedStep } from '@/services/autoblocks/types';
import type { IntegrationDiscoveryProposal } from '@/services/integrations/types';

export type MissionRiskLevel = 'safe' | 'elevated' | 'critical';

export type MissionRiskCategory =
  | 'proposal_only'
  | 'credential_connect'
  | 'external_write'
  | 'deploy_publish'
  | 'spend_or_billing'
  | 'pii_export'
  | 'browser_automation'
  | 'custom_unverified'
  | 'live_execution';

export type MissionRiskItem = {
  id: string;
  level: MissionRiskLevel;
  category: MissionRiskCategory;
  summary: string;
  detail?: string;
  source: 'plan_step' | 'plan_connector' | 'discovery' | 'live_run';
  sourceRef: string;
};

export type MissionRiskApproval = {
  approvedAt: string;
  note?: string;
};

export const MISSION_RISK_LABELS: Record<
  MissionRiskLevel,
  { label: string; description: string }
> = {
  safe: {
    label: 'Safe',
    description: 'Proposals, scaffolds, dry-run — no approval needed to continue planning',
  },
  elevated: {
    label: 'Needs approval',
    description: 'Connect credentials, write externally, or use unverified custom connectors',
  },
  critical: {
    label: 'High risk',
    description: 'Deploy, spend, bulk PII export, or live execution with side effects',
  },
};

export const MISSION_CREATIVE_FREEDOM_NOTICE = `The AI may honestly devise any approach needed to complete the Mission — including novel connectors, steps, and integrations. Safe proposals proceed to review; elevated and critical items require explicit human approval before use.`;

const WRITE_SUBTYPES =
  /\b(send|post|publish|delete|update|charge|pay|upload|submit|apply|message|notify|webhook_out)\b/i;
const PII_SUBTYPES = /\b(export|sync|share|forward|upload).*(contact|resume|pii|personal)/i;
const BROWSER_SUBTYPES = /\b(scrape|browser|puppet|playwright|selenium)\b/i;

export function requiresHumanApproval(level: MissionRiskLevel): boolean {
  return level === 'elevated' || level === 'critical';
}

export function riskLevelFromStep(step: PlannedStep): MissionRiskLevel {
  const explicit = (step as PlannedStep & { riskLevel?: MissionRiskLevel }).riskLevel;
  if (explicit === 'safe' || explicit === 'elevated' || explicit === 'critical') {
    return explicit;
  }

  const hay = `${step.label} ${step.subtype} ${step.rationale}`.toLowerCase();

  if (step.type === 'action' && WRITE_SUBTYPES.test(hay)) {
    if (PII_SUBTYPES.test(hay)) return 'critical';
    return 'elevated';
  }
  if (BROWSER_SUBTYPES.test(hay)) return 'elevated';
  if (step.subtype === 'http' && /POST|PUT|PATCH|DELETE/i.test(String(step.config.method ?? ''))) {
    return 'elevated';
  }
  return 'safe';
}

export function riskCategoryFromStep(step: PlannedStep): MissionRiskCategory {
  const hay = `${step.label} ${step.subtype} ${step.rationale}`.toLowerCase();
  if (PII_SUBTYPES.test(hay)) return 'pii_export';
  if (BROWSER_SUBTYPES.test(hay)) return 'browser_automation';
  if (WRITE_SUBTYPES.test(hay)) return 'external_write';
  if (/deploy|publish|production/i.test(hay)) return 'deploy_publish';
  if (/stripe|billing|charge|payment/i.test(hay)) return 'spend_or_billing';
  return 'proposal_only';
}

export function riskFromConnectorAuth(authType: string, connectorId: string): MissionRiskLevel {
  const auth = authType.toLowerCase();
  if (auth === 'none' || auth === 'simulated') return 'safe';
  if (connectorId.startsWith('ab.custom.')) return 'elevated';
  return 'elevated';
}

export function extractRiskItemsFromPlan(plan: OutcomePlan): MissionRiskItem[] {
  const items: MissionRiskItem[] = [];

  for (const step of plan.steps) {
    const level = riskLevelFromStep(step);
    if (!requiresHumanApproval(level)) continue;
    items.push({
      id: `risk:step:${step.id}`,
      level,
      category: riskCategoryFromStep(step),
      summary: step.label,
      detail: step.rationale,
      source: 'plan_step',
      sourceRef: step.id,
    });
  }

  for (const conn of plan.connectors) {
    const level = riskFromConnectorAuth(conn.authType, conn.id);
    if (!requiresHumanApproval(level)) continue;
    items.push({
      id: `risk:connector:${conn.id}`,
      level,
      category: conn.id.startsWith('ab.custom.') ? 'custom_unverified' : 'credential_connect',
      summary: `Connect ${conn.name}`,
      detail: conn.purpose,
      source: 'plan_connector',
      sourceRef: conn.id,
    });
  }

  for (const built of plan.builtConnectors ?? []) {
    items.push({
      id: `risk:connector:${built.id}`,
      level: 'elevated',
      category: 'custom_unverified',
      summary: `Use AI-built connector ${built.name}`,
      detail: built.description,
      source: 'plan_connector',
      sourceRef: built.id,
    });
  }

  items.push({
    id: 'risk:live_run:mission',
    level: 'critical',
    category: 'live_execution',
    summary: 'Run Mission live (real side effects)',
    detail: 'Approve only after dry-run passes and elevated items above are accepted.',
    source: 'live_run',
    sourceRef: plan.id,
  });

  return dedupeRiskItems(items);
}

export function extractRiskFromDiscoveryProposal(
  proposal: IntegrationDiscoveryProposal
): MissionRiskItem | null {
  const auth = proposal.connector.auth;
  if (auth === 'none') return null;
  return {
    id: `risk:discovery:${proposal.id}`,
    level: 'elevated',
    category: 'custom_unverified',
    summary: `Use discovered integration ${proposal.name}`,
    detail: proposal.description,
    source: 'discovery',
    sourceRef: proposal.id,
  };
}

function dedupeRiskItems(items: MissionRiskItem[]): MissionRiskItem[] {
  const seen = new Set<string>();
  return items.filter((i) => {
    if (seen.has(i.id)) return false;
    seen.add(i.id);
    return true;
  });
}

export function getPendingRiskApprovals(
  items: MissionRiskItem[],
  approvals: Record<string, MissionRiskApproval>
): MissionRiskItem[] {
  return items.filter((i) => requiresHumanApproval(i.level) && !approvals[i.id]?.approvedAt);
}

export function allRequiredRiskApprovalsGranted(
  items: MissionRiskItem[],
  approvals: Record<string, MissionRiskApproval>
): boolean {
  return getPendingRiskApprovals(items, approvals).length === 0;
}
